import { useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Menu, X, User, LogOut, Shield, ShoppingBag, Activity, Home } from "lucide-react";
import AuthModal from "./AuthModal";

const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const NAV_LINKS = [
  { path: "/", label: "Home", icon: Home },
  { path: "/products", label: "Products", icon: ShoppingBag },
  { path: "/status", label: "Status", icon: Activity },
];

const NavLink = ({ path, label, isActive, onClick }) => (
  <Link
    to={path}
    className={`nav-link text-sm uppercase tracking-wider ${isActive ? "active" : ""}`}
    onClick={onClick}
    data-testid={`nav-${label.toLowerCase()}`}
  >
    {label}
  </Link>
);

const MobileNavLink = ({ path, label, icon: Icon, isActive, onClick }) => (
  <Link
    to={path}
    className={`flex items-center gap-3 text-sm uppercase tracking-wider ${isActive ? "text-[#4F7CFF]" : "text-[#A1A1AA]"}`}
    onClick={onClick}
  >
    <Icon className="w-5 h-5" />
    {label}
  </Link>
);

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const handleAuthClick = useCallback((mode) => {
    setAuthMode(mode);
    setAuthModalOpen(true);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate("/");
  }, [logout, navigate]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050508]/90 backdrop-blur-xl border-b border-white/5" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <span className="font-heading text-xl text-white tracking-tight">NEXUS<span className="text-[#4F7CFF]">FORGE</span></span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {NAV_LINKS.map(({ path, label }) => (
                <NavLink key={path} path={path} label={label} isActive={location.pathname === path} />
              ))}
              <a
                href="https://discord.gg/efeySGKGJk"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link text-sm uppercase tracking-wider flex items-center gap-2"
                data-testid="nav-discord"
              >
                <DiscordIcon className="w-4 h-4" />
                Discord
              </a>
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <>
                  <Link to="/dashboard" className="nav-link text-sm uppercase tracking-wider flex items-center gap-2" data-testid="nav-dashboard">
                    <User className="w-4 h-4" />
                    {user.username}
                  </Link>
                  {user.is_admin && (
                    <Link to="/admin" className="text-[#4F7CFF] text-sm uppercase tracking-wider flex items-center gap-2" data-testid="nav-admin">
                      <Shield className="w-4 h-4" />
                      Admin
                    </Link>
                  )}
                  <button onClick={handleLogout} className="text-[#A1A1AA] hover:text-white transition-colors" data-testid="logout-btn">
                    <LogOut className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => handleAuthClick("login")} className="btn-secondary text-sm" data-testid="login-btn">Login</button>
                  <button onClick={() => handleAuthClick("register")} className="btn-primary text-sm" data-testid="register-btn">Sign Up</button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(prev => !prev)} data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-[#0a0a12] border-t border-white/5">
            <div className="px-4 py-4 space-y-4">
              {NAV_LINKS.map(({ path, label, icon }) => (
                <MobileNavLink key={path} path={path} label={label} icon={icon} isActive={location.pathname === path} onClick={closeMobileMenu} />
              ))}
              <a
                href="https://discord.gg/efeySGKGJk"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm uppercase tracking-wider text-[#A1A1AA]"
              >
                <DiscordIcon className="w-5 h-5" />
                Discord
              </a>
              {user ? (
                <>
                  <MobileNavLink path="/dashboard" label="Dashboard" icon={User} isActive={false} onClick={closeMobileMenu} />
                  {user.is_admin && (
                    <Link to="/admin" className="flex items-center gap-3 text-sm uppercase tracking-wider text-[#4F7CFF]" onClick={closeMobileMenu}>
                      <Shield className="w-5 h-5" />
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={() => { handleLogout(); closeMobileMenu(); }}
                    className="flex items-center gap-3 text-sm uppercase tracking-wider text-[#A1A1AA]"
                  >
                    <LogOut className="w-5 h-5" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-4 pt-4">
                  <button onClick={() => { handleAuthClick("login"); closeMobileMenu(); }} className="btn-secondary text-sm flex-1">Login</button>
                  <button onClick={() => { handleAuthClick("register"); closeMobileMenu(); }} className="btn-primary text-sm flex-1">Sign Up</button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} mode={authMode} setMode={setAuthMode} />
    </>
  );
};

export default Navbar;
